import type { ProfilePreference } from '@elara/domain/models/profile-preferences';
import type { PreferenceCategory, TaggedPreference } from '@elara/domain/models/schemas/shared-schemas';
import type { TravelerProfile } from '@elara/domain/models/schemas/TravelerProfileSchema';

import { useEffect, useRef } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { isListedTravelCreditCard, TRAVEL_CREDIT_CARD_OPTIONS } from '@elara/client-core/lib/preference-utils';
import { cn } from '@elara/client-core/lib/utils';
import { ZodGoogleCabinClass } from '@elara/domain/models/schemas/zod-types';

import { useCurrentUserProfile, useRouterState, useUserProfileState } from '#states/hooks.js';
import { Breadcrumbs } from '#ui/Breadcrumbs.js';
import { Checkbox } from '#ui/checkbox.js';
import { Card, CardContent, CardHeader, CardTitle, Label, Loader } from '#ui/index.js';
import { PreferenceList } from '#ui/PreferenceList.js';
import { SavedPreferenceList } from '#ui/SavedPreferenceList.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';

const TITLE_CLASSES = 'text-xl font-regular';

export function TravelPreferencesPage() {
  const profile = useCurrentUserProfile();
  const profileState = useUserProfileState();
  const routerState = useRouterState();
  const hasInitializedEditing = useRef(false);
  /** Saves run one at a time, each sending the latest form state, so a slow save can't land last. */
  const saveChain = useRef<Promise<void>>(Promise.resolve());

  // Load the editing copy once. Every change saves as it's made, so re-copying on each profile snapshot
  // would throw away a change made while an earlier save was still on its way.
  useEffect(() => {
    if (!profile || hasInitializedEditing.current) return;
    hasInitializedEditing.current = true;
    profileState.initializeEditing();
  }, [profile, profileState]);

  if (!profile) {
    return (
      <AppLayout
        backButtonRoute={ROUTES.ACCOUNT}
        title={<AppLayoutTitle title="Travel Preferences" />}
        transitionKey={routerState.currentRoute?.path}
      >
        <div className="flex items-center justify-center h-full">
          <Loader loading={true} />
        </div>
      </AppLayout>
    );
  }

  const error = profileState.error;
  const editingProfile = profileState.editingProfile;

  /** Applies a change to the cabin classes or credit cards and saves it straight away. */
  const saveTravelerProfile = (changes: Partial<TravelerProfile>) => {
    profileState.updateField('travelerProfile', { ...profileState.editingProfile.travelerProfile, ...changes });
    saveChain.current = saveChain.current.then(async () => profileState.save());
  };

  // Saved preferences come from the live profile, not the editing copy: each change saves on its own
  // (see SavedPreferenceList), so the list reflects what's stored, including facts Elara remembers.
  const savedPreferences = profile.travelerProfile?.preferences ?? [];
  const preferenceList = (category: PreferenceCategory, label: string, placeholder: string, showLabel = false) => (
    <SavedPreferenceList
      label={label}
      showLabel={showLabel}
      placeholder={placeholder}
      entries={entriesIn(savedPreferences, category)}
      onAdd={async (value) => profileState.addPreference({ value, category })}
      onUpdate={async (entry, value) => profileState.updatePreference(entry.id, value)}
      onRemove={async (entry) => profileState.removePreference(entry)}
    />
  );

  return (
    <AppLayout
      backButtonRoute={ROUTES.ACCOUNT}
      title={<AppLayoutTitle title="Travel Preferences" />}
      transitionKey={routerState.currentRoute?.path}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-16 flex flex-col gap-6">
            <div className="flex flex-col gap-3 mb-2">
              <Breadcrumbs
                items={[{ label: 'Account', to: ROUTES.ACCOUNT }, { label: 'Travel Preferences' }]}
                className="mb-3"
              />
              <h1 className="text-2xl xl:text-3xl font-display font-light">Travel Preferences</h1>
              <p className="text-muted-foreground">Help Elara recommend the right flights, hotels, and experiences.</p>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-md text-sm mb-6">
                {error.message}
              </div>
            )}

            <Card className="shadow-sm p-6 dark:bg-background-secondary">
              <CardHeader>
                <CardTitle className={TITLE_CLASSES}>Flights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Domestic flight class</Label>
                      <CabinClassPicker
                        value={editingProfile.travelerProfile?.domesticFlightClass || []}
                        onChange={(v) =>
                          saveTravelerProfile({ domesticFlightClass: v as TravelerProfile['domesticFlightClass'] })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>International flight class</Label>
                      <CabinClassPicker
                        value={editingProfile.travelerProfile?.internationalFlightClass || []}
                        onChange={(v) =>
                          saveTravelerProfile({
                            internationalFlightClass: v as TravelerProfile['internationalFlightClass'],
                          })
                        }
                      />
                    </div>
                  </div>

                  {preferenceList('airport', 'Preferred airports', 'Add an airport', true)}
                </div>
              </CardContent>
            </Card>

            <PreferenceCard title="Hotels">
              {preferenceList('hotel', 'Hotel preferences', 'Add a hotel preference')}
            </PreferenceCard>

            <PreferenceCard title="Destinations">
              {preferenceList('destination', 'Destinations', 'Add a destination')}
            </PreferenceCard>

            <PreferenceCard title="Dining">
              {preferenceList('dining', 'Dining', 'Add a dining preference')}
            </PreferenceCard>

            <PreferenceCard title="Dietary">
              {preferenceList('dietary', 'Dietary', 'Add a dietary preference')}
            </PreferenceCard>

            <PreferenceCard title="Key dates">
              {preferenceList('key_date', 'Dates that matter for trip planning', 'Add a key date')}
            </PreferenceCard>

            <PreferenceCard
              title="Travel credit cards"
              description="Elara uses these to find perks and offers only available to cardholders."
            >
              <TravelCreditCardPicker
                value={editingProfile.travelerProfile?.travelCreditCards ?? []}
                onChange={(cards) => saveTravelerProfile({ travelCreditCards: cards })}
              />
            </PreferenceCard>

            <PreferenceCard title="General">
              {preferenceList('general', 'Other preferences', 'Add a preference')}
            </PreferenceCard>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

const CABIN_CLASS_OPTIONS = ZodGoogleCabinClass.options;

function CabinClassPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (opt: string) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };

  return (
    <div className="flex flex-col gap-2">
      {CABIN_CLASS_OPTIONS.map((opt) => {
        const checked = value.includes(opt);
        return (
          <label
            key={opt}
            className={cn(
              'flex items-center gap-2 cursor-pointer rounded-sm border px-3 py-2 text-sm',
              checked ? 'border-accent' : 'border-input',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => toggle(opt)}
              className={cn(checked && 'text-accent border-accent data-[state=checked]:bg-accent')}
            />
            <span>{opt === 'Premium economy' ? 'Premium Economy' : opt}</span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * The cards onboarding offers, as checkboxes, plus a free-text list for any other card (including
 * entries typed before the checkboxes existed). Stored as one `travelCreditCards` list either way.
 */
function TravelCreditCardPicker({
  value,
  onChange,
}: {
  value: TaggedPreference[];
  onChange: (cards: TaggedPreference[]) => void;
}) {
  const listed = value.filter((card) => isListedTravelCreditCard(card.value));
  const others = value.filter((card) => !isListedTravelCreditCard(card.value));
  const toggle = (option: string) => {
    const isChecked = listed.some((card) => card.value === option);
    onChange(
      isChecked
        ? value.filter((card) => card.value !== option)
        : [...listed, { value: option, source: 'user' }, ...others],
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        {TRAVEL_CREDIT_CARD_OPTIONS.map((option) => {
          const checked = listed.some((card) => card.value === option);
          return (
            <label
              key={option}
              className={cn(
                'flex items-center gap-2 cursor-pointer rounded-sm border px-3 py-2 text-sm',
                checked ? 'border-accent' : 'border-input',
              )}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => toggle(option)}
                className={cn(checked && 'text-accent border-accent data-[state=checked]:bg-accent')}
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>
      <PreferenceList
        label="Other cards"
        value={others}
        onChange={(next) => onChange([...listed, ...next])}
        placeholder="Add another card"
      />
    </div>
  );
}

/** Preferences of one category, in profile order; entries saved before categories existed read as general. */
function entriesIn(preferences: ProfilePreference[], category: PreferenceCategory): ProfilePreference[] {
  return preferences.filter((entry) => ('category' in entry ? entry.category : 'general') === category);
}

function PreferenceCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="shadow-sm p-6 dark:bg-background-secondary">
      <CardHeader>
        <CardTitle className={TITLE_CLASSES}>{title}</CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
